import { useMsal } from '@azure/msal-react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import StyledButton from '../../components/shared/Button/StyledButton';
import Logo from '../../components/shared/Logo/Logo';
import { loginRequest } from '../../config/msalConfig';
import useAuth from '../../hooks/useAuth';
import { useTranslation } from '../../hooks/useTranslation';
import { getInvite, InviteDetails } from '../../services/inviteService';

const PENDING_TOKEN_KEY = 'elora_invite_pending_token';

const InviteAccept: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { instance } = useMsal();
  const { t } = useTranslation();
  const { currentUser, isLoading: authIsLoading } = useAuth();

  const [invite, setInvite] = useState<InviteDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  // True once this tab has clicked "Accept Invite" for this exact token and
  // is waiting for the B2C sign-up redirect to complete. We use a dedicated
  // flag (rather than relying on the page staying on /dashboard after
  // sign-up) because a stale cached MSAL session can bounce the browser back
  // to this URL — without this guard the page just re-fetches the invite,
  // which the backend has by then marked as consumed, and shows "expired"
  // instead of completing the sign-up.
  const isAwaitingOwnSignup =
    sessionStorage.getItem(PENDING_TOKEN_KEY) === token;

  useEffect(() => {
    if (isAwaitingOwnSignup && currentUser) {
      sessionStorage.removeItem(PENDING_TOKEN_KEY);
    }
  }, [isAwaitingOwnSignup, currentUser]);

  useEffect(() => {
    if (!token) {
      setError(true);
      setIsLoading(false);
      return;
    }
    getInvite(token)
      .then(setInvite)
      .catch(() => setError(true))
      .finally(() => setIsLoading(false));
  }, [token]);

  const handleAccept = () => {
    if (!token || !invite) return;
    sessionStorage.setItem('elora_invite_token', token);
    sessionStorage.setItem(PENDING_TOKEN_KEY, token);

    // If a different account is cached/active in this browser (e.g. via
    // SSO), B2C would silently sign the user back in as that account
    // instead of the invited identity. Force a fresh credential prompt so
    // they can sign in/up as the invited email - but do NOT log out, since
    // that would tear down the cached session for any other account (and
    // any other tab) using this browser.
    const activeAccountEmail = instance.getActiveAccount()?.username;
    const needsDifferentIdentity =
      instance.getAllAccounts().length > 0 &&
      activeAccountEmail?.toLowerCase() !== invite.email.toLowerCase();

    instance.loginRedirect({
      ...loginRequest,
      prompt: needsDifferentIdentity ? 'login' : undefined,
    });
  };

  if (isAwaitingOwnSignup && currentUser) {
    return <Navigate to="/dashboard" replace />;
  }

  if (isLoading || (isAwaitingOwnSignup && authIsLoading)) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !invite) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        p={4}
      >
        <Logo />
        <Typography variant="h6" color="error" sx={{ mt: 3 }}>
          {t('This invite has expired or is no longer valid.')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      p={4}
    >
      <Logo />
      <Stack spacing={2} sx={{ mt: 4, maxWidth: 400, textAlign: 'center' }}>
        <Typography variant="h5">{invite.company_name}</Typography>
        <Typography variant="body1">{invite.inviter_name}</Typography>
        <Typography variant="body2" color="text.secondary">
          {invite.email}
        </Typography>
        <StyledButton
          variant="contained"
          color="primary"
          onClick={handleAccept}
        >
          {t('Accept Invite')}
        </StyledButton>
      </Stack>
    </Box>
  );
};

export default InviteAccept;
